// src/state/auth.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

export const API_BASE = 'https://api.kypser.com/api';
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken') || '');
  const [tabActiveHotelId, setSelectedHotelIdState] = useState(sessionStorage.getItem('tabHotelId') || null);

  const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
  });

  const setSelectedHotelId = (id) => {
    if (id) {
      sessionStorage.setItem('tabHotelId', id);
    } else {
      sessionStorage.removeItem('tabHotelId');
    }
    setSelectedHotelIdState(id);
  };

  // Load user on mount if token exists
  useEffect(() => {
    if (accessToken) {
      me().then(u => {
        // If no tab-local hotel is selected yet, use the one from the token
        if (!sessionStorage.getItem('tabHotelId') && u?.hotel_id) {
          setSelectedHotelId(String(u.hotel_id));
        }
      }).catch(err => console.error("Failed to fetch user on mount", err));
    }
  }, []);

  // Sync tabActiveHotelId with user.hotel_id if it's not set yet
  useEffect(() => {
    if (user?.hotel_id && !tabActiveHotelId) {
      setSelectedHotelId(String(user.hotel_id));
    }
  }, [user, tabActiveHotelId]);

  // Attach token except for login/refresh
  api.interceptors.request.use((config) => {
    const url = (config.url || '').toLowerCase();
    if (url.includes('/auth/login') || url.includes('/auth/refresh')) return config;
    const token = localStorage.getItem('accessToken') || accessToken;
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Handle 401 (try refresh) and 403 (immediate logout)
  api.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config || {};
      const status = error?.response?.status;
      const url = (original.url || '').toLowerCase();
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

      // 403 Forbidden - immediately clear auth and redirect
      if (status === 403 && !isAuthEndpoint) {
        localStorage.removeItem('accessToken');
        setAccessToken('');
        setUser(null);
        return Promise.reject(error);
      }

      // 401 Unauthorized - try to refresh token once
      if (status === 401 && !original._retried && !isAuthEndpoint) {
        original._retried = true;
        try {
          const r = await api.post('/auth/refresh');
          const newToken = r?.data?.accessToken;
          if (!newToken) throw new Error('No access token from refresh');
          localStorage.setItem('accessToken', newToken);
          setAccessToken(newToken);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api.request(original);
        } catch {
          localStorage.removeItem('accessToken');
          setAccessToken('');
          setUser(null);
        }
      }
      return Promise.reject(error);
    }
  );

  async function login(email, password) {
    const res = await api.post('/auth/login', { email, password });
    const token = res.data?.accessToken;
    if (!token) throw new Error('No access token in login response');
    localStorage.setItem('accessToken', token);
    setAccessToken(token);
    const meRes = await api.get('/me');
    const u = meRes.data?.user || null;
    setUser(u);

    // Identify target property: sessionStorage first, then user's default hotel_id
    const targetHotelId = sessionStorage.getItem('tabHotelId') || (u?.hotel_id ? String(u.hotel_id) : null);
    
    if (targetHotelId && targetHotelId !== 'all') {
      try {
        await switchProperty(targetHotelId);
      } catch (err) {
        console.error("Failed to switch to initial property during login", err);
        setSelectedHotelId(targetHotelId); // Fallback to just setting state if switch fails
      }
    } else {
      setSelectedHotelId(targetHotelId);
    }
    return u;
  }

  async function logout() {
    try { await api.post('/auth/logout'); } finally {
      localStorage.removeItem('accessToken');
      sessionStorage.removeItem('tabHotelId');
      setAccessToken('');
      setUser(null);
      setSelectedHotelIdState(null);
    }
  }

  async function me() {
    const res = await api.get('/me');
    setUser(res.data?.user || null);
    return res.data?.user || null;
  }

  async function switchProperty(hotelId) {
    const res = await api.post('/auth/switch-property', { hotelId });
    const token = res.data?.accessToken;
    if (!token) throw new Error('No access token in switch response');
    localStorage.setItem('accessToken', token);
    setAccessToken(token);
    setSelectedHotelId(String(hotelId));
    // Refresh user data to reflect the new hotel context
    const meRes = await api.get('/me');
    setUser(meRes.data?.user || null);
    return meRes.data?.user || null;
  }

  async function updateToken(newToken) {
    if (!newToken) return;
    localStorage.setItem('accessToken', newToken);
    setAccessToken(newToken);
    // Refresh user data to reflect the new context
    const meRes = await api.get('/me');
    setUser(meRes.data?.user || null);
    return meRes.data?.user || null;
  }

  return (
    <AuthCtx.Provider value={{
      user,
      accessToken,
      login,
      logout,
      me,
      switchProperty,
      updateToken,
      api,
      tabActiveHotelId,
      setSelectedHotelId
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

